import i18n from 'i18next';
import { assign, createMachine, sendParent } from 'xstate';
import { NavigationSections } from '../constants';
import { QueryParamKeys } from '../constants/query-params';
import { APP_FINISHED_LEASE_STATUSES } from '../constants/app-status';
import { AppStatuses, ChannelTypes, FormTypes, ProtectionPlusStatus } from '../gql/graphql';
import { getApplicationDetails } from '../helpers/converters/getApplicationDetails';
import { getCartFromOrderDetails } from '../helpers/converters/getCartFromOrderDetails';
import { canSkipItemEntry, getEcommApprovalOnly } from '../helpers/leases/leases.helpers';
import { redirectToApplicationDetails, setAppStatusParams } from '../helpers/url/url.helpers';
import getApplicationItems from '../networkRequests/cart/getApplicationItems';
import updateOrderItems from '../networkRequests/cart/updateOrderItems';
import getIpAddress from '../networkRequests/ip/getIpAddress';
import acknowledgeLeaseOptions from '../networkRequests/lease/acknowledgeLeaseOptions';
import generateContract from '../networkRequests/lease/generateContract';
import getExistingLeaseDocuments from '../networkRequests/lease/getExistingLeaseDocuments';
import getProtectionPlusEnrollmentUrl from '../networkRequests/lease/getProtectionPlusEnrollmentUrl';
import selectLeaseOption from '../networkRequests/lease/selectLeaseOption';
import signLeaseAgreement from '../networkRequests/lease/signLeaseAgreement';
import queryOrder from '../networkRequests/order/queryOrder';
import queryEmbedUrl, { GetEmbedUrlResult } from '../networkRequests/virtualCard/getEmbedURL';
import removeVirtualCard from '../networkRequests/virtualCard/removeVirtualCard';
import { merchantRoles } from '../types/role';
import { EcommMachineEvents, ValidatedByTokenType } from './types/ecommMachine.types';
import getApplicationId from '../utils/commonState/getApplicationId';
import { PropagateNetworkErrors } from './actions/genericActions';
import {
  AssignExistingLeaseDocuments,
  AssignGeneratedLeaseDocuments,
  AssignInitialLeaseDocumentLanguage,
  AssignRequestedLeaseDocumentLanguage,
  createLeaseDocumentLanguageReconciliation,
  createLanguageChangedTransitions,
} from './actions/leaseDocumentLanguage';
import { identify, page, track } from './actions/segment';
import { trackContractSummaryAcknowledgeFailed } from './actions/trackContractSummaryAcknowledgeFailed';
import { MakeStateCallingQueryOrder, MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import {
  PostDecisionEvents,
  PostDecisionMachineContext,
  PostDecisionMachineEvents,
  PostDecisionMachineGuards,
  PostDecisionMachineStates,
  PostDecisionStates,
} from './types/postDecisionMachine.types';
import { canAccessContractSummaryPage, canAccessMerchantContractSummaryPage } from '../helpers/contractSummaryAccess/contractSummaryAccess';

// Sessions with contract summary access (param + elevated role or contract token) view the
// existing signed lease documents rather than generating a contract. No status check: the routing
// states below only reach it once the status is established, so the callers scope it.
const hasContractSummaryAccess = (ctx: PostDecisionMachineContext) => canAccessContractSummaryPage(ctx) || canAccessMerchantContractSummaryPage(ctx);

const canViewExistingLeaseDocuments = (ctx: PostDecisionMachineContext) => ctx.highestSupportedApplicationMilestone > 3 && hasContractSummaryAccess(ctx);

// The customer arrived on the emailed OOBA link and the dealer is M4 - the same gate EntryPointV2
// puts on its OOBA transitions. Pre-M4 OOBA sessions keep routing on the dealer shape.
const isOobaSigningSession = (ctx: PostDecisionMachineContext) =>
  ctx.validatedByTokenType === ValidatedByTokenType.OOBA && ctx.highestSupportedApplicationMilestone > 3;

const isLongFormDealer = (ctx: PostDecisionMachineContext) => ctx.dealer?.formType === FormTypes.Long || ctx.dealer?.formType === FormTypes.LongV2;
const isLongFormEcomm = (ctx: PostDecisionMachineContext) => isLongFormDealer(ctx) && ctx.dealer?.channelType === ChannelTypes.Ecommerce;

const isOtb = (ctx: PostDecisionMachineContext) => !!ctx.application?.lease?.isOtb;

const vcOrderHasItems = (ctx: PostDecisionMachineContext) => !!ctx.dealer?.isVirtualCard && !!ctx.orderDetails?.items?.length;

const hasCartProblem = (ctx: PostDecisionMachineContext) => !!ctx.application?.lease?.cartValidationAdditionalDetails?.cartValidationType;

const appStatus = (ctx: PostDecisionMachineContext) => getApplicationDetails(ctx.application)?.status;

// An installment loan has no lease contract. It stops at the approval page (ecomm) or the approval
// confirmation (brick and mortar) at every status, never a contract summary, the lease agreement or
// the virtual card page: the long form machine's PostApprovalBM / IsEcommPostIP did the same before
// those pages moved here.
const isInstallmentLoan = (ctx: PostDecisionMachineContext) => !!ctx.application?.installmentLoan;
const isEcommInstallmentLoan = (ctx: PostDecisionMachineContext) => isInstallmentLoan(ctx) && ctx.dealer?.channelType === ChannelTypes.Ecommerce;

// Brick-and-mortar signing can be deferred to the merchant's device. Lifted from
// long-form-application-machine, which was the only copy that had it.
const canBypassBDSPage = (context: PostDecisionMachineContext) => {
  const bdsFF = context.ldClient.variation('originations-disable-customer-device-contract-signing-202511', false);
  return !bdsFF || context.contractSummaryPageParam;
};

export default createMachine<PostDecisionMachineContext, PostDecisionMachineEvents, PostDecisionMachineStates>(
  {
    id: 'PostDecision',
    initial: PostDecisionStates.Entry,
    context: {} as PostDecisionMachineContext, // Nothing you put here matters, the init block is the data in the `src: PostDecisionMachine` line in the parent
    on: {
      [PostDecisionEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [PostDecisionEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [PostDecisionEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      // Highest level conditionals and skips
      [PostDecisionStates.Entry]: {
        always: [
          // skipToPage overrides the status routing below, for handoffs where status alone lands
          // elsewhere: BACK to the cart, the initial payment handoff (context may still read
          // preApproved, and the virtual card single flow lands on Approval where a resume with
          // items would skip to the contract summary), the return from the debit card update.
          // Internal events once the parents are deleted (OR-2998/OR-2999).
          { cond: 'SkipToCartSummary', target: PostDecisionStates.CartSummary },
          { cond: 'SkipToApproval', target: PostDecisionStates.Approval },
          { cond: 'SkipToContractSummaryEcomm', target: PostDecisionStates.ContractSummaryEcomm },
          { cond: 'SkipToContractSummaryBM', target: PostDecisionStates.ContractSummaryBM },
          { cond: 'SkipToApprovalConfirmation', target: PostDecisionStates.ApprovalConfirmation },
          // An approved virtual card session requeries with cart validation before it routes, the
          // way existing-application-machine's VirtualCardApplication did on entry. RouteApproved
          // reads cartValidationType, which only that query returns, to keep an order with a cart
          // problem off the contract summary.
          {
            cond: (ctx) => !!ctx.dealer?.isVirtualCard && ctx.application?.lease?.status === AppStatuses.Approved,
            target: PostDecisionStates.VirtualCardCartCheck,
          },
          { cond: 'StatusApproved', target: PostDecisionStates.RouteApproved },
          { cond: 'StatusDocsReady', target: PostDecisionStates.RouteDocsReady },
          { cond: 'StatusFinished', target: PostDecisionStates.RouteFinished },

          // Unreachable. OR-2997 only routes apps in approved / docsReady / a finished status, and
          // each branch above is total for its status, so this fires only once that invariant
          // breaks. Fail loudly rather than guessing at a page; dropping the default instead would
          // stall here and render nothing at all.
          {
            target: '#PostDecision.InternalError',
            actions: (context, event) => track('Internal Error Occurred - PostDecision', { status: appStatus(context) })(context, event),
          },
        ],
      },

      [PostDecisionStates.VirtualCardCartCheck]: MakeStateCallingQueryOrderWithCartValidation(PostDecisionStates.RouteApproved),

      // Total: the last transition is unconditional. Order is load bearing.
      [PostDecisionStates.RouteApproved]: {
        always: [
          // installment loans stop at the approval pages (see isInstallmentLoan)
          { cond: isEcommInstallmentLoan, target: PostDecisionStates.Approval },
          { cond: isInstallmentLoan, target: PostDecisionStates.ApprovalConfirmation },

          { cond: 'BrickAndMortarContractSummaryReady', target: PostDecisionStates.ContractSummaryBM },

          // A mobile embedded VC dealer with contract summaries belongs on the contract summary: the
          // cart is already confirmed, so it skips every approval page below, the otb one included
          // (the ecomm machine's canSkipItemEntry branch outranked its otb branch the same way).
          { cond: 'CanSkipItemEntry', target: PostDecisionStates.ContractSummaryEcomm },

          { cond: isOtb, target: PostDecisionStates.Approval },

          { cond: 'BrickAndMortarWithoutContractSummaryAccess', target: PostDecisionStates.ApprovalConfirmation },

          // Approval-only dealers stop at the approval confirmation - the merchant completes the
          // rest, so they never see a contract summary or the lease agreement.
          { cond: 'ApprovalOnly', target: PostDecisionStates.ApprovalConfirmation },

          { cond: 'VirtualCardEcommCartReady', target: PostDecisionStates.ContractSummaryEcomm },

          { cond: 'VirtualCardDealer', target: PostDecisionStates.Approval },
          // long form uses Approval as the ELF landing page on resume/refresh; it never enters a
          // contract summary straight from entry (long form's IsEcommPostIP)
          { cond: isLongFormEcomm, target: PostDecisionStates.Approval },

          {
            cond: (ctx) => !!ctx.application?.lease?.contractSummaries?.length,
            target: PostDecisionStates.ContractSummaryEcomm,
          },
          // An M4 brick and mortar session with contract summary access but no contract summaries
          // yet. The merchant opened the contract summary link before any items were added, so there
          // is nothing to summarize: it waits on the approval confirmation, never the ecomm page.
          { cond: 'IsBrickAndMortar', target: PostDecisionStates.ApprovalConfirmation },
          {
            target: PostDecisionStates.Approval,
          },
        ],
      },

      // Total: the last transition is unconditional. Order is load bearing. Every branch above the
      // default is a session that gets past the approval confirmation: a signing session, contract
      // summary access, or a form that re-acknowledges the contract summary. Whatever provides none
      // of those stops at the confirmation.
      [PostDecisionStates.RouteDocsReady]: {
        always: [
          { cond: isEcommInstallmentLoan, target: PostDecisionStates.Approval },
          { cond: isInstallmentLoan, target: PostDecisionStates.ApprovalConfirmation },

          // The emailed OOBA link is how an M4 brick and mortar customer reaches signing. Replaces the
          // EntryPointV2 transition that hands these sessions to long form with skipToPage
          // LeaseAgreement, which honours the same BDS split.
          { cond: (ctx) => isOobaSigningSession(ctx) && canBypassBDSPage(ctx), target: PostDecisionStates.LeaseAgreement },
          { cond: isOobaSigningSession, target: PostDecisionStates.DeferContractSigning },

          // contract summary access (param + elevated role or contract token) generates a contract;
          // merchant access, which the param branch does not cover, views the existing documents
          { cond: canAccessContractSummaryPage, target: PostDecisionStates.LeaseAgreement },
          { cond: canViewExistingLeaseDocuments, target: '#PostDecision.LeaseAgreement.callGetExistingLeaseDocuments' },

          // Virtual card and the modal short form ecomm session show the contract summary again and
          // sign from its NEXT: the ecomm machine's EntryPointContractSummary sent them back to that
          // page, and existing-application-machine's VirtualCardApplication landed on it for a
          // docsReady VC lease of either channel. The full screen short form ecomm session is
          // approval only (getEcommApprovalOnly) and falls to the default.
          { cond: 'VirtualCardDealer', target: PostDecisionStates.ContractSummaryEcomm },
          {
            cond: (ctx) => ctx.dealer?.formType === FormTypes.Short && ctx.dealer?.channelType === ChannelTypes.Ecommerce && !getEcommApprovalOnly(ctx.dealer),
            target: PostDecisionStates.ContractSummaryEcomm,
          },

          // A pre-M4 ecomm long form refresh at docsReady lands on Approval, and its NEXT walks back
          // through the contract summary to regenerate. The long form machine only did this below
          // M4 (its docsReady && M4 branches signed first, and IsEcommPostIP came after them).
          // Ecomm is pinned to M3 without params or tokens (ecomm-machine CheckChannel), so the M4
          // arm below is a customer holding a contract summary link or a contract token without
          // the param.
          { cond: (ctx) => isLongFormEcomm(ctx) && ctx.highestSupportedApplicationMilestone < 4, target: PostDecisionStates.Approval },

          // An M4 long form session on the customer's own device: docs already generated, so sign
          // here, or defer to the merchant's device when the BDS flag is on (the long form
          // machine's docsReady && M4 pair).
          {
            cond: (ctx) => isLongFormDealer(ctx) && ctx.highestSupportedApplicationMilestone >= 4 && canBypassBDSPage(ctx),
            target: PostDecisionStates.LeaseAgreement,
          },
          { cond: (ctx) => isLongFormDealer(ctx) && ctx.highestSupportedApplicationMilestone >= 4, target: PostDecisionStates.DeferContractSigning },

          // Nothing above applies: the pre-M4 store, the short form brick and mortar customer on
          // their own device, the full screen short form ecomm session. The merchant finishes the
          // lease.
          { target: PostDecisionStates.ApprovalConfirmation },
        ],
      },

      // Total: the last transition is unconditional. Order is load bearing.
      [PostDecisionStates.RouteFinished]: {
        always: [
          { cond: isInstallmentLoan, target: PostDecisionStates.LeaseSigned },
          // post-signing re-entry with contract summary access shows the signed agreement read-only
          // instead of the "all done" page, so it precedes both pages below
          { cond: canViewExistingLeaseDocuments, target: '#PostDecision.LeaseAgreement.callGetExistingLeaseDocuments' },
          { cond: 'VirtualCardDealer', target: PostDecisionStates.VirtualCardPage },
          { target: PostDecisionStates.LeaseSigned },
        ],
      },

      [PostDecisionStates.Approval]: {
        // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
          waiting: {
            on: {
              [PostDecisionEvents.NEXT]: [
                // A shop-first order's cart is already confirmed, so the contract summary is next.
                { cond: (ctx) => vcOrderHasItems(ctx) && !hasCartProblem(ctx), target: '#PostDecision.ContractSummaryEcomm' },
                // The approval page's "Adjust cart" button: the cart is rebuilt from the order so the
                // customer can edit it. The cart review re-validates through updateOrderItems and
                // shows it again if it still stands.
                {
                  cond: vcOrderHasItems,
                  target: '#PostDecision.CartSummary',
                  actions: assign({ cart: (ctx) => getCartFromOrderDetails(ctx.orderDetails) }),
                },
                { cond: 'VirtualCardDealer', target: '#PostDecision.ItemEntry' },
                // for elf, approval is only reachable for resume/refresh flows. This acts like a landing page before showing the contract summary
                { cond: 'IsEcommerce', target: '#PostDecision.ContractSummaryEcomm' },
                // b&m does not have a transition here because that is handled by ooba in a separate session
                { target: '#PostDecision.ApprovalConfirmation' },
              ],
            },
          },
        },
        meta: {
          canGoBackViaHeader: false,
        },
      },

      [PostDecisionStates.ItemEntry]: {
        on: {
          [PostDecisionEvents.BACK]: {
            target: PostDecisionStates.CartSummary,
            actions: assign({ canGoBackViaHeader: false, itemToEdit: null }),
          },
          [PostDecisionEvents.NEXT]: [
            {
              cond: (context: PostDecisionMachineContext) => context.itemToEdit !== null,
              target: PostDecisionStates.CartSummary,
              actions: [
                assign({
                  cart: (context: PostDecisionMachineContext, event) => context.cart.toSpliced(context.itemToEdit!.index, 1, event.data),
                  itemToEdit: null,
                  canGoBackViaHeader: false,
                }),
                track('Item Updated'),
              ],
            },
            {
              target: PostDecisionStates.CartSummary,
              actions: [
                assign({ cart: (context: PostDecisionMachineContext, event) => [...context.cart, event.data], canGoBackViaHeader: false }),
                track('Item Added'),
              ],
            },
          ],
        },
        meta: {
          navigationSection: NavigationSections.PAYMENT_DETAILS,
        },
      },

      [PostDecisionStates.CartSummary]: {
        // remove blocked items from errors list and from cart. This shouldnt do anything in phase 2 because aes will not return blocks
        entry: assign((context) => {
          if (context.cartValidationItemErrors) {
            track('Autoremoved items', {
              items: Object.values(context.cartValidationItemErrors)
                .map((v) => v.displayName)
                .join(', '),
            });

            return {
              autoremovedCartItemNames: Object.values(context.cartValidationItemErrors).map((v) => v.displayName!),
              cartValidationItemErrors: {},
              cart: context.cart.filter((item) => !context.cartValidationItemErrors?.[item.id]), // return items in the cart that dont have a matching cv item block
            };
          }
          return {}; // this makes no changes to context, it does not assign a blank context
        }),

        on: {
          [PostDecisionEvents.BACK]: [
            {
              cond: 'HasItems',
              target: PostDecisionStates.ItemEntry,
              actions: assign({ canGoBackViaHeader: true }),
            },
            {
              target: PostDecisionStates.ItemEntry,
            },
          ],
          [PostDecisionEvents.EDIT_CART_ITEM]: {
            target: PostDecisionStates.ItemEntry,
            actions: assign({ itemToEdit: (_context, event) => event.data.item, canGoBackViaHeader: true }),
          },
          [PostDecisionEvents.DELETE_CART_ITEM]: [
            // if deleting last item, transition to itemEntry after
            {
              cond: (ctx) => ctx.cart.length === 1,
              target: PostDecisionStates.ItemEntry,
              actions: assign({ cart: (context, event) => context.cart.toSpliced(event.data.index, 1) }),
            },
            { actions: assign({ cart: (context, event) => context.cart.toSpliced(event.data.index, 1) }) },
          ],
          [PostDecisionEvents.NEXT]: {
            target: PostDecisionStates.CartTotal,
          },
        },
        meta: {
          navigationSection: NavigationSections.PAYMENT_DETAILS,
        },
      },

      [PostDecisionStates.CartTotal]: {
        on: {
          [PostDecisionEvents.NEXT]: {
            target: PostDecisionStates.CartReviewLoading,
            actions: assign({ cartTotal: (_, event) => event.data.cartTotal }),
          },
          [PostDecisionEvents.BACK]: PostDecisionStates.CartSummary,
        },
        meta: {
          navigationSection: NavigationSections.PAYMENT_DETAILS,
          canGoBackViaHeader: true,
        },
      },

      [PostDecisionStates.CartReviewLoading]: {
        initial: 'loading',
        states: {
          loading: {
            tags: 'callingUpdateOrderItems',
            invoke: {
              src: updateOrderItems,
              onDone: [
                // TODO: handle orderErrors
                {
                  cond: (_, event) => !!event.data.orderErrors?.length,
                  target: '#PostDecision.CartSummary',
                  actions: assign({
                    updateOrderErrors: (_, event) => event.data?.updateOrderErrors,
                    cartValidationErrors: (_, event) => event.data?.cartValidationErrors,
                    cartValidationItemErrors: (_, event) => event.data?.cartValidationItemErrors,
                    cartValidationItemWarnings: (_, event) => event.data?.cartValidationItemWarnings,
                  }),
                },
                // item level errors
                {
                  cond: (_, event) => !!event.data.cartValidationItemErrors && Object.keys(event.data.cartValidationItemErrors).length > 0,
                  // || Object.keys(event.data.cartValidationItemWarnings).length > 0, // warnings do not block yet
                  target: '#PostDecision.CartSummary',
                  actions: [
                    assign({
                      cartValidationErrors: (_, event) => event.data?.cartValidationErrors,
                      cartValidationItemErrors: (_, event) => event.data?.cartValidationItemErrors,
                      cartValidationItemWarnings: (_, event) => event.data?.cartValidationItemWarnings,
                    }),
                    track('Cart validation item error'),
                  ],
                },
                // cart level errors
                {
                  cond: (_, event) => !!event.data.cartValidationErrors?.length,
                  target: '#PostDecision.CartError',
                  actions: [
                    assign({
                      cartValidationErrors: (_, event) => event.data?.cartValidationErrors,
                      cartValidationItemErrors: (_, event) => event.data?.cartValidationItemErrors,
                      cartValidationItemWarnings: (_, event) => event.data?.cartValidationItemWarnings,
                    }),
                    track('Cart validation cart error'),
                  ],
                },
                {
                  target: 'callingQueryOrder', // calling this here to use the pattern of moving the loader out of the contract summary
                  // only assign items here because this mutation only returns the details
                  actions: assign({ orderDetails: (context, event) => ({ ...context.orderDetails, items: event.data?.order?.details?.items }) }),
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('#PostDecision.ContractSummaryEcomm'),
        },
      },

      [PostDecisionStates.CartError]: {
        on: {
          [PostDecisionEvents.EDIT_CART]: {
            target: '#PostDecision.CartSummary',
            actions: assign({ cartValidationItemWarnings: {}, updateOrderErrors: [] }),
          },
        },
      },

      [PostDecisionStates.ContractSummaryEcomm]: {
        // The VC copy skipped this requery ("all codepaths implicitly requery the order"); the lease
        // and long-form copies kept it as a precaution to ensure contract summaries exist. Keeping
        // the requery is the safe superset.
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrder('waiting'),
          waiting: {
            on: {
              [PostDecisionEvents.NEXT]: {
                target: 'callingSelectLeaseOption',
                actions: assign({ selectLeaseOptionErrors: [] }),
              },
            },
          },
          callingSelectLeaseOption: {
            tags: 'calling',
            invoke: {
              src: selectLeaseOption,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: '#PostDecision.InternalError',
                },
                { target: '#PostDecision.LeaseAgreement' },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
        on: {
          // BACK differed per host: VC went to the cart, long-form went to the approval page, and the
          // short-form lease branch had no handler at all (so no fallback transition here).
          [PostDecisionEvents.BACK]: [
            {
              // refresh handler to update the cart with the order items
              cond: (context) => !!context.dealer?.isVirtualCard && !context?.cart?.length,
              target: PostDecisionStates.CartSummary,
              actions: assign({ cart: (context) => getCartFromOrderDetails(context.orderDetails) }),
            },
            { cond: 'VirtualCardDealer', target: PostDecisionStates.CartSummary },
            { cond: 'IsLongForm', target: PostDecisionStates.Approval },
          ],
          [PostDecisionEvents.EDIT_DEBIT_INFO]: {
            target: PostDecisionStates.ExitToDebitCardEntry,
            actions: assign({ fromContractSummary: true }),
          },
        },
        meta: {
          navigationSection: NavigationSections.REVIEW_AND_PAY,
        },
      },

      [PostDecisionStates.ContractSummaryBM]: {
        initial: 'callingQueryOrder',
        entry: assign({ showNotAcknowledgedModal: false }),
        states: {
          // Just a precaution to ensure contract summaries exist
          callingQueryOrder: MakeStateCallingQueryOrder('callingGetAppItems'),
          callingGetAppItems: {
            tags: 'calling',
            invoke: {
              src: getApplicationItems,
              onDone: {
                target: 'waiting',
                actions: assign({
                  applicationItems: (_, event) => {
                    return event.data || [];
                  },
                }),
              },
              onError: { actions: PropagateNetworkErrors },
            },
          },
          waiting: {
            on: {
              [PostDecisionEvents.NEXT]: [
                {
                  target: 'callingAcknowledgeLeaseOptions',
                  actions: assign({
                    selectLeaseOptionErrors: [],
                    acknowledgeLeaseTermsErrors: [],
                    customerSelectedLeaseTerm: (_, event) => event.data as unknown as string,
                  }),
                },
              ],
              [PostDecisionEvents.REFRESH_CONTRACT_SUMMARY_STATUS]: {
                target: 'refreshingOrderStatus',
                actions: assign({ showNotAcknowledgedModal: false }),
              },
              [PostDecisionEvents.CLOSE_NOT_ACKNOWLEDGED_MODAL]: {
                actions: assign({ showNotAcknowledgedModal: false }),
              },
            },
          },
          callingAcknowledgeLeaseOptions: {
            tags: 'calling',
            invoke: {
              src: acknowledgeLeaseOptions,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: '#PostDecision.InternalError',
                },
                {
                  cond: (_, event) => event.data.acknowledgeLeaseTermsErrors?.length,
                  target: 'waiting',
                  actions: [
                    assign({ acknowledgeLeaseTermsErrors: (_context, event) => event.data.acknowledgeLeaseTermsErrors ?? [] }),
                    trackContractSummaryAcknowledgeFailed,
                  ],
                },
                { target: 'callingSelectLeaseOption', actions: track('Contract Summary Acknowledge Successful') },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          refreshingOrderStatus: {
            tags: 'calling',
            invoke: {
              src: queryOrder,
              onDone: {
                target: 'checkRefreshResult',
                actions: assign({ application: (_, event) => event.data?.application, orderDetails: (_, event) => event.data?.details }),
              },
              onError: {
                target: 'waiting',
                actions: PropagateNetworkErrors,
              },
            },
          },
          checkRefreshResult: {
            always: [
              {
                cond: (context) => context.application?.lease?.status === 'docsReady',
                target: '#PostDecision.LeaseAgreement',
              },
              {
                // the VC copy omitted this clause; the lease and long-form copies both had it
                cond: (context) => context.application?.lease?.status === 'docsSigned',
                target: '#PostDecision.MerchantRedirect',
              },
              {
                target: 'waiting',
                actions: assign({ showNotAcknowledgedModal: true }),
              },
            ],
          },
          callingSelectLeaseOption: {
            tags: 'calling',
            invoke: {
              src: selectLeaseOption,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: '#PostDecision.InternalError',
                },
                {
                  // only long-form had the deferred-signing branch
                  cond: (ctx) => !canBypassBDSPage(ctx),
                  target: '#PostDecision.DeferContractSigning',
                },
                { target: '#PostDecision.LeaseAgreement' },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
        on: {
          [PostDecisionEvents.EDIT_DEBIT_INFO]: {
            target: PostDecisionStates.ExitToDebitCardEntry,
            actions: assign({ fromContractSummary: true }),
          },
        },
        meta: {
          navigationSection: NavigationSections.REVIEW_AND_PAY,
        },
      },

      [PostDecisionStates.DeferContractSigning]: {
        initial: 'callingGenerateContract',
        states: {
          callingGenerateContract: {
            invoke: {
              src: (context, _) => generateContract(context.orderId),
              onDone: [
                {
                  cond: (_, event) => event.data?.generateContractErrors?.length,
                  actions: [
                    assign({ generateContractErrors: (_, event: any) => event.data?.generateContractErrors }),
                    track('Internal Error Occurred - Lease Agreement'),
                  ],
                },
                {
                  actions: assign({ leaseDocuments: (_, event: any) => event.data?.leaseDocuments }),
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
      },

      [PostDecisionStates.LeaseAgreement]: {
        initial: 'callingGenerateContract',
        // OR-2886: the documents follow the footer language toggle. Same wiring as the long form
        // and existing application copies (see actions/leaseDocumentLanguage.ts).
        entry: AssignInitialLeaseDocumentLanguage,
        on: {
          [PostDecisionEvents.LANGUAGE_CHANGED]: {
            cond: (_context, event) => !!event.data?.language,
            actions: AssignRequestedLeaseDocumentLanguage,
          },
        },
        states: {
          callingGenerateContract: {
            // its own tag: the page stays mounted with a document-level loader while regenerating
            tags: 'callingGenerateContract',
            invoke: {
              src: (context, _) => generateContract(context.orderId),
              onDone: [
                {
                  cond: (_, event) => event.data?.generateContractErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [
                    assign({ generateContractErrors: (_, event: any) => event.data?.generateContractErrors }),
                    track('Internal Error Occurred - Lease Agreement'),
                  ],
                },
                {
                  target: 'waiting',
                  actions: AssignGeneratedLeaseDocuments,
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
            on: {
              [PostDecisionEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
            },
          },
          waiting: {
            always: createLeaseDocumentLanguageReconciliation(),
            on: {
              // Short form resolves the signer's IP first; long form signs without one.
              [PostDecisionEvents.NEXT]: [{ cond: 'IsShortForm', target: 'callingGetIp' }, { target: 'callingSignLeaseAgreement' }],
              [PostDecisionEvents.REFRESH_SIGNING_STATUS]: 'refreshingSigningStatus',
              [PostDecisionEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
            },
          },
          callingGetIp: {
            tags: 'calling',
            invoke: {
              src: getIpAddress,
              onDone: [{ target: 'callingSignLeaseAgreement', actions: assign({ ipAddress: (_, event) => event.data.ipAddress }) }],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          callingSignLeaseAgreement: {
            tags: 'calling',
            invoke: {
              src: (context) => signLeaseAgreement(context.application?.lease?.id, context.ipAddress || undefined),
              onDone: [
                {
                  cond: (_, event) => event.data?.signingErrors?.length,
                  target: 'waiting',
                  actions: [assign({ signingErrors: (_, event: any) => event.data?.signingErrors }), track('Signing Error')],
                },
                {
                  target: 'routeAfterSigning',
                  // OR-2774: tracked here rather than on the LeaseSigned page, because
                  // merchant and Protection Plus routes never render it (see routeAfterSigning).
                  actions: [assign({ leaseSigned: true }), track('Lease Agreement Sign Successful')],
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          refreshingSigningStatus: {
            tags: 'calling',
            invoke: {
              src: queryOrder,
              onDone: {
                target: 'checkSigningResult',
                actions: assign({ application: (_: any, event: any) => event.data?.application, orderDetails: (_: any, event: any) => event.data?.details }),
              },
              onError: {
                target: 'waiting',
                actions: PropagateNetworkErrors,
              },
            },
          },
          checkSigningResult: {
            always: [
              {
                cond: 'StatusDocsSigned',
                target: 'routeAfterSigning',
              },
              {
                target: 'waiting',
              },
            ],
          },
          routeAfterSigning: {
            always: [
              // the VC copy went straight to the card page, skipping the P+ and merchant checks
              {
                cond: 'VirtualCardDealer',
                target: '#PostDecision.VirtualCardPage',
              },
              {
                cond: 'OfferProtectionPlus',
                target: 'routeToProtectionPlus',
              },
              {
                cond: (context) => merchantRoles.includes(context.role),
                target: '#PostDecision.MerchantRedirect',
              },
              {
                target: '#PostDecision.LeaseSigned',
              },
            ],
          },
          routeToProtectionPlus: {
            tags: 'calling',
            invoke: {
              src: (context) => getProtectionPlusEnrollmentUrl(context.application?.lease?.id!),
              onDone: [
                {
                  cond: (_, event) => event.data?.errors?.length > 0,
                  target: '#PostDecision.LeaseSigned', //should there be an error screen saying error occured getting P+ enrollment url?
                  actions: assign({ protectionPlusEnrollmentErrors: (_, event) => event.data.errors }),
                },
                {
                  target: '#PostDecision.ProtectionPlusRedirect',
                  actions: (_, event) => {
                    const url = new URL(event.data.url);
                    url.searchParams.set(QueryParamKeys.Language, i18n.resolvedLanguage ?? 'en');
                    window.location.href = url.toString();
                  },
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          // this state is only reachable via the contractSummaryPage transition
          callGetExistingLeaseDocuments: {
            tags: 'calling',
            invoke: {
              src: (context, _) => getExistingLeaseDocuments(context.orderId),
              onDone: [
                {
                  cond: (_, event) => event.data?.getExistingLeaseDocumentsErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [
                    assign({ getExistingLeaseDocumentsErrors: (_, event: any) => event.data?.getExistingLeaseDocumentsErrors }),
                    track('Internal Error Occurred - Getting Lease Agreement'),
                  ],
                },
                {
                  target: 'waiting',
                  // OR-2725: opens in the signed language and caches the English companion (if any);
                  // OR-3375: the footer toggle then moves the document between the two
                  actions: AssignExistingLeaseDocuments,
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
        meta: { navigationSection: NavigationSections.REVIEW_AND_PAY },
      },

      [PostDecisionStates.VirtualCardPage]: {
        initial: 'select',
        states: {
          // if there's no items, requery the order to get those items. This happens for refresh/view virtual card flow because there's no query order in that flow
          select: { always: [{ cond: (context) => !context.orderDetails?.items?.length, target: 'callingQueryOrder' }, { target: 'callingGetEmbedUrl' }] },
          callingQueryOrder: MakeStateCallingQueryOrder('callingGetEmbedUrl'),
          callingGetEmbedUrl: {
            tags: 'calling',
            invoke: {
              src: (context: PostDecisionMachineContext) => queryEmbedUrl(context.orderId),
              onDone: [
                {
                  target: 'waiting',
                  actions: assign((_context: any, event: { data: GetEmbedUrlResult }) => ({
                    cardExpiration: event.data.cardExpiration,
                    embedUrl: event.data.embedUrl,
                    embedUrlError: event.data.embedUrlError,
                  })),
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          waiting: {
            on: {
              [PostDecisionEvents.RETRY]: {
                target: 'callingGetEmbedUrl',
                actions: assign({ embedUrlError: null }),
              },
              [PostDecisionEvents.REMOVE_VIRTUAL_CARD]: {
                target: 'callingRemoveVirtualCard',
              },
            },
          },
          callingRemoveVirtualCard: {
            tags: 'calling',
            invoke: {
              src: (context: PostDecisionMachineContext) => removeVirtualCard(getApplicationId(context.application)),
              onDone: [
                {
                  cond: (_context, event) => !!event?.data?.removeVirtualCardErrors?.length,
                  target: 'waiting',
                  actions: assign({ removeVirtualCardErrors: (_context, event) => event.data.removeVirtualCardErrors }),
                },
                {
                  cond: (context) => !context?.cart?.length,
                  target: '#PostDecision.CartSummary',
                  actions: [
                    assign({ embedUrl: '', embedUrlError: null, leaseSigned: false, cart: (context) => getCartFromOrderDetails(context.orderDetails) }),
                    () => setAppStatusParams('approved'),
                  ],
                },
                {
                  target: '#PostDecision.CartSummary',
                  actions: [assign({ embedUrl: '', embedUrlError: null, leaseSigned: false }), () => setAppStatusParams('approved')],
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
      },
      [PostDecisionStates.ApprovalConfirmation]: {},
      [PostDecisionStates.InternalError]: {},
      [PostDecisionStates.LeaseSigned]: {},
      [PostDecisionStates.MerchantRedirect]: {
        entry: (context) => redirectToApplicationDetails(context.application?.lease?.publicId),
      },
      [PostDecisionStates.ProtectionPlusRedirect]: {},
      // The debit card update link on the contract summary. The card entry page lives in the
      // application machines, so the parent picks the one for the dealer's form type (long form,
      // or the existing application machine for short form and VC) and each hands the session back
      // here afterwards with skipToPage ContractSummaryEcomm / ContractSummaryBM.
      [PostDecisionStates.ExitToDebitCardEntry]: {
        type: 'final',
        entry: sendParent((context) => ({ type: EcommMachineEvents.EDIT_DEBIT_INFO, data: { ...context } })),
      },
    },
    schema: {
      context: {} as PostDecisionMachineContext,
      events: {} as PostDecisionMachineEvents,
      guards: {} as PostDecisionMachineGuards,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      log: (context, event, meta) => {
        console.log('Post Decision Machine');
        console.log('current state:', meta.state?.value);
        console.log('context', context);
        console.log('event', event);
      },
    },
    guards: {
      VirtualCardDealer: (context: PostDecisionMachineContext) => !!context.dealer?.isVirtualCard,
      IsShortForm: (context: PostDecisionMachineContext) => context.dealer?.formType === FormTypes.Short,
      IsLongForm: (context: PostDecisionMachineContext) => context.dealer?.formType === FormTypes.Long || context.dealer?.formType === FormTypes.LongV2,
      IsEcommerce: (context: PostDecisionMachineContext) => context.dealer?.channelType === ChannelTypes.Ecommerce,
      IsBrickAndMortar: (context: PostDecisionMachineContext) => context.dealer?.channelType === ChannelTypes.BrickAndMortar,
      SkipToCartSummary: (context: PostDecisionMachineContext) => context.skipToPage === PostDecisionStates.CartSummary,
      SkipToApproval: (context: PostDecisionMachineContext) => context.skipToPage === PostDecisionStates.Approval,
      SkipToContractSummaryEcomm: (context: PostDecisionMachineContext) => context.skipToPage === PostDecisionStates.ContractSummaryEcomm,
      SkipToContractSummaryBM: (context: PostDecisionMachineContext) => context.skipToPage === PostDecisionStates.ContractSummaryBM,
      SkipToApprovalConfirmation: (context: PostDecisionMachineContext) => context.skipToPage === PostDecisionStates.ApprovalConfirmation,

      // derived entry guards
      // The status dispatch in CheckLeaseStatus. These three are the only guards that read the
      // application status - everything below is a pure shape predicate, scoped by the branch it is
      // used in.
      StatusApproved: (context: PostDecisionMachineContext) => appStatus(context) === AppStatuses.Approved,
      StatusDocsReady: (context: PostDecisionMachineContext) => appStatus(context) === AppStatuses.DocsReady,
      StatusFinished: (context: PostDecisionMachineContext) => APP_FINISHED_LEASE_STATUSES.includes(appStatus(context) as AppStatuses),

      // Absorbed from the ecomm machine's EntryPointApprovalConfirmation. getEcommApprovalOnly is
      // full-screen ecomm non-VC (it returns false inside an iframe, where the flow does continue
      // to the contract summary). The ecomm arm is short form only: long form never reached that
      // guard (its Loading branches sat above it), so long form keeps its own derivation.
      // The second arm - pre-M4 brick and mortar - applies to both forms.
      ApprovalOnly: (context: PostDecisionMachineContext) =>
        (context.dealer?.formType === FormTypes.Short && getEcommApprovalOnly(context.dealer)) ||
        (context.dealer?.channelType === ChannelTypes.BrickAndMortar && context.highestSupportedApplicationMilestone < 4),
      // The M4 contract summary, reached from the emailed OOBA link or with contract summary access.
      // No channel check: tele-sales ecomm apps come here too (OR-2891 / OR-3004). A plain M4 B&M
      // session is neither and stays on the approval confirmation.
      // Contract summaries are priced from the application items, so a lease without them has no
      // items yet and nothing to summarize.
      BrickAndMortarContractSummaryReady: (context: PostDecisionMachineContext) =>
        context.highestSupportedApplicationMilestone > 3 &&
        (isOobaSigningSession(context) || hasContractSummaryAccess(context)) &&
        !!context.application?.lease?.contractSummaries?.length,
      BrickAndMortarWithoutContractSummaryAccess: (context: PostDecisionMachineContext) =>
        context.dealer?.channelType === ChannelTypes.BrickAndMortar &&
        !(canAccessContractSummaryPage(context) || canAccessMerchantContractSummaryPage(context)),
      // replaces the 'ApprovalWithSkipItemEntry' starting page
      CanSkipItemEntry: (context: PostDecisionMachineContext) =>
        canSkipItemEntry(context.dealer, !!context.application?.lease?.contractSummaries, context.entrypointParam ?? ''),
      // VC ecomm resume with a confirmed cart
      VirtualCardEcommCartReady: (context: PostDecisionMachineContext) =>
        vcOrderHasItems(context) && !hasCartProblem(context) && context.dealer.channelType === ChannelTypes.Ecommerce,
      StatusDocsSigned: (context: PostDecisionMachineContext) => appStatus(context) === 'docsSigned',

      HasItems: (context: PostDecisionMachineContext) => context.cart.length > 0,

      OfferProtectionPlus: (context: PostDecisionMachineContext) =>
        context.highestSupportedApplicationMilestone >= 4 &&
        context.dealer.channelType === ChannelTypes.BrickAndMortar &&
        !!context.dealer.isLTOInsurance &&
        !['UT', 'OK', 'IN', 'AK', 'RI'].includes(context.dealer.dealerAddress.state) &&
        context.application?.customer?.protectionPlusStatus != ProtectionPlusStatus.Enrolled &&
        context.application?.customer?.protectionPlusStatus != ProtectionPlusStatus.OptedIn,
    },
  },
);
